import React, { useState } from 'react';
import { Button, Snackbar, Callout, useNotifications } from '@otp/ui';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useResourcesIndex } from 'src/hooks/query/resources/use-resources-index';
import {
  Actions,
  AppArea,
  ResourcePageListFilterInput,
  Subjects,
} from 'src/graphql/generated/graphql';
import { Tools } from '@otp/icons';
import { Can } from 'src/utils/can';

interface ResourcesIndexButtonProps {
  filter?: ResourcePageListFilterInput;
  className?: string;
}

export const ResourcesIndexButton: React.FC<ResourcesIndexButtonProps> = ({
  filter,
  className,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [indexResources, { loading }] = useResourcesIndex();
  const notifications = useNotifications();

  const handleIndex = async () => {
    try {
      const result = await indexResources({
        variables: {
          filter,
        },
      });

      const [indexed, failed] = result.data?.indexResources as [number, number];

      notifications.show(
        t(
          'resources.index.result',
          'Successfully indexed {{indexed}} resources with {{failed}} failures',
          { indexed, failed },
        ),
        {
          severity: failed > 0 ? 'error' : 'success',
        },
      );
    } catch (error) {
      notifications.show(
        t('resources.index.error', 'Failed to index resources'),
        {
          severity: 'error',
        },
      );
    }
  };


  return (
    <Can I={Actions.Write} a={Subjects.Resources} appArea={AppArea.Admin}>
      <Button
        variant="contained"
        disabled={loading}
        onClick={handleIndex}
        className={className}
        startIcon={<Tools color={theme.palette.primary.contrastText} />}
      >
        {loading
          ? t('resources.list.indexing', 'Indexing...')
          : t('resources.list.index', 'Index Resources')}
      </Button>
    </Can>
  );
};
